import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import {
  Button,
  Container,
  Typography,
  Stack,
  CircularProgress,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import Iconify from "src/components/iconify/Iconify";
import PackVersionReminder from "src/components/pack/PackVersionReminder";
import LanguageTabs from "src/components/language/LanguageTabs";
import { useNavigate, useParams } from "react-router-dom";
import { get, post } from "src/utils/networkUtils";
import { createBlobInContainer } from "src/utils/azureBlob";
import useWithUrlTenant from "src/hooks/useTenant";
import { getEnabledLangs } from "src/langs/langs";

const PACK_TYPES = [
  { value: 0, label: "Base11 (Welcome / Starter Pack)" },
  { value: 17, label: "DailyPack1 (Free Daily Pack)" },
  { value: 18, label: "AllAccessPassAllStar1 (All Access Pass All Star)" },
  { value: 19, label: "AllAccessPassDay1" },
  { value: 20, label: "AllAccessPassDay2" },
  { value: 21, label: "AllAccessPassDay3" },
  { value: 22, label: "AllAccessPassDay4" },
  { value: 23, label: "AllAccessPassDay5" },
  { value: 24, label: "AllAccessPassDay6" },
];

const ASSET_LANGS = getEnabledLangs();

const defaultAsset = {
  packType: 0,
  imageI18n: { en: "" },
  animationI18n: { en: "" },
};

function I18nTabsSection({
  title,
  subheader,
  field,
  i18nValues,
  onUpload,
  uploading,
  accept,
  showImagePreview,
}) {
  const [activeTab, setActiveTab] = useState(0);
  const lang = ASSET_LANGS[activeTab];

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader title={title} subheader={subheader} />
      <Divider />
      <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
        <LanguageTabs
          langs={ASSET_LANGS}
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          ariaLabel={`${title.toLowerCase()} language tabs`}
          idPrefix={`pack-asset-${field}-tab`}
          variant="scrollable"
          scrollButtons="auto"
        />
      </Box>
      <CardContent>
        <Tooltip title={`Upload for ${lang.label}`}>
          <Button
            variant="outlined"
            component="label"
            disabled={uploading[lang.value]}
            startIcon={
              uploading[lang.value] ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Iconify icon="eva:upload-outline" />
              )
            }
          >
            {uploading[lang.value] ? "Uploading…" : `Upload ${lang.label}`}
            <input
              key={lang.value}
              hidden
              accept={accept}
              type="file"
              onChange={(e) => onUpload(e, lang.value)}
            />
          </Button>
        </Tooltip>

        <Box sx={{ mt: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
          {ASSET_LANGS.map((l) => {
            const url = i18nValues?.[l.value];
            const fileName = url ? url.split("/").pop() : null;
            return (
              <Box key={l.value} sx={{ textAlign: "center" }}>
                {showImagePreview ? (
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: 1,
                      overflow: "hidden",
                      border: "1px solid",
                      borderColor: url ? "divider" : "grey.200",
                      mb: 0.5,
                      background: "#f4f6f8",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {url ? (
                      <img
                        src={url}
                        alt={`${l.label} preview`}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        onError={(e) => { e.target.style.opacity = "0.3"; }}
                      />
                    ) : (
                      <Iconify icon="eva:image-outline" sx={{ color: "text.disabled", width: 28, height: 28 }} />
                    )}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      px: 1.5,
                      py: 0.75,
                      mb: 0.5,
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: url ? "success.light" : "grey.200",
                      background: url ? "success.lighter" : "#f4f6f8",
                      minWidth: 160,
                      maxWidth: 260,
                    }}
                  >
                    <Iconify
                      icon={url ? "eva:checkmark-circle-2-outline" : "eva:alert-circle-outline"}
                      sx={{ color: url ? "success.main" : "text.disabled", flexShrink: 0 }}
                    />
                    <Typography
                      variant="caption"
                      noWrap
                      title={fileName || "No file uploaded"}
                      color={url ? "text.primary" : "text.disabled"}
                    >
                      {fileName || "No file uploaded"}
                    </Typography>
                  </Box>
                )}
                <Stack direction="row" alignItems="center" justifyContent="center">
                  <Typography variant="caption" color="text.secondary">
                    {l.label}
                  </Typography>
                </Stack>
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
}

export default function PackAssetPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const withTenant = useWithUrlTenant();
  const isNew = id === "new";

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [asset, setAsset] = useState(defaultAsset);
  const [uploadingImage, setUploadingImage] = useState({});
  const [uploadingAnim, setUploadingAnim] = useState({});
  const defaultLocale = process.env.REACT_APP_DEFAULT_LOCALE || 'en';
  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    get(`/admin/packs/assets`)
      .then((res) => {
        const data = (res.assets || []).find(
          (a) => String(a.packAssetId) === String(id)
        );
        if (data) {
          setAsset({
            packType: data.packType ?? "Base11",
            imageI18n: parseI18n(data.imageI18n),
            animationI18n: parseI18n(data.animationI18n),
          });
        }
      })
      .finally(() => setLoading(false));
  }, [id, isNew]);

  const parseI18n = (value) => {
    if (!value) return { en: "" };
    if (typeof value === "string") {
      try {
        return JSON.parse(value);
      } catch {
        return { en: value };
      }
    }
    return value;
  };

  const handleI18nChange = (field, lang, value) => {
    setAsset((prev) => ({
      ...prev,
      [field]: { ...prev[field], [lang]: value },
    }));
  };

  const handleImageUpload = async (event, lang) => {
    const input = event.target;
    if (!input.files?.length) return;
    const file = input.files[0];
    setUploadingImage((prev) => ({ ...prev, [lang]: true }));
    try {
      await createBlobInContainer(file);
      const containerName = process.env.REACT_APP_STORAGE_CONTAINER;
      const storageAccountName = process.env.REACT_APP_STORAGERESOURCENAME;
      handleI18nChange(
        "imageI18n",
        lang,
        `https://${storageAccountName}.blob.core.windows.net/${containerName}/${file.name}`
      );
    } catch (err) {
      console.error("Image upload failed:", err);
    } finally {
      setUploadingImage((prev) => ({ ...prev, [lang]: false }));
      input.value = "";
    }
  };

  const handleAnimationUpload = async (event, lang) => {
    const input = event.target;
    if (!input.files?.length) return;
    const file = input.files[0];
    setUploadingAnim((prev) => ({ ...prev, [lang]: true }));
    try {
      await createBlobInContainer(file);
      const containerName = process.env.REACT_APP_STORAGE_CONTAINER;
      const storageAccountName = process.env.REACT_APP_STORAGERESOURCENAME;
      handleI18nChange(
        "animationI18n",
        lang,
        `https://${storageAccountName}.blob.core.windows.net/${containerName}/${file.name}`
      );
    } catch (err) {
      console.error("Animation upload failed:", err);
    } finally {
      setUploadingAnim((prev) => ({ ...prev, [lang]: false }));
      input.value = "";
    }
  };

  const canSave = () => {
    if (asset.packType == null) return false;
    if (!asset.imageI18n?.[defaultLocale]?.trim() || !asset.animationI18n?.[defaultLocale]?.trim()) return false;
    return true;
  };

  const handleSave = () => {
    if (!canSave()) return;
    setSaving(true);
    const payload = {
      packType: asset.packType,
      imageI18n: asset.imageI18n,
      animationI18n: asset.animationI18n,
      packAssetId: isNew ? null : id,
    };
    const request = post(`/admin/packs/assets/upsert`, payload);

    request
      .then(() => navigate(withTenant(`/dashboard/pack-assets`)))
      .catch((err) => console.error("Save failed:", err))
      .finally(() => setSaving(false));
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  return (
    <>
      <Helmet>
        <title>{isNew ? "New Pack Asset" : "Edit Pack Asset"}</title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton onClick={() => navigate(withTenant(`/dashboard/pack-assets`))}>
              <Iconify icon="eva:arrow-back-outline" />
            </IconButton>
            <Typography variant="h4">
              {isNew ? "New Pack Asset" : "Edit Pack Asset"}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              onClick={() => navigate(withTenant(`/dashboard/pack-assets`))}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={
                saving ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Iconify icon="eva:save-outline" />
                )
              }
              onClick={handleSave}
              disabled={!canSave() || saving}
            >
              Save
            </Button>
          </Stack>
        </Stack>
        <PackVersionReminder sx={{ mb: 3 }} />

        {/* Pack Type */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="pack-type-label">Pack Type *</InputLabel>
                  <Select
                    labelId="pack-type-label"
                    value={asset.packType}
                    label="Pack Type *"
                    onChange={(e) =>
                      setAsset((prev) => ({ ...prev, packType: e.target.value }))
                    }
                  >
                    {PACK_TYPES.map((pt) => (
                      <MenuItem key={pt.value} value={pt.value}>
                        {pt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Image I18n */}
        <I18nTabsSection
          title="Image"
          subheader="Pack image per language. English (EN) is required."
          field="imageI18n"
          i18nValues={asset.imageI18n}
          onUpload={handleImageUpload}
          uploading={uploadingImage}
          accept="image/*"
          showImagePreview
        />

        {/* Animation I18n */}
        <I18nTabsSection
          title="Animation"
          subheader="Pack animation per language (Lottie JSON, MP4, etc.). English (EN) is required."
          field="animationI18n"
          i18nValues={asset.animationI18n}
          onUpload={handleAnimationUpload}
          uploading={uploadingAnim}
          accept=".json,.mp4,.webm,.gif,video/*"
          showImagePreview={false}
        />
      </Container>
    </>
  );
}
